import { effect, inject, Injectable, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { BrandingService } from './branding.service';
import { DEFAULT_BRANDING } from './branding.constant';

export const formatTitle = (page: string | undefined, brandName: string) =>
  [page, brandName].filter(Boolean).join(' | ');

/**
 * Browser tab title: `"<route title> | <brand name>"`, or just the brand name
 * for routes without a `title`. With no name configured it uses the default
 * brand's (`DEFAULT_BRANDING`). Re-renders when the branding refresh lands after the first
 * navigation.
 */
@Injectable({
  providedIn: 'root',
})
export class BrandingTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly branding = inject(BrandingService);
  /**
   * `null` until the first navigation. The router creates this strategy
   * during bootstrap, while a first visit is still waiting on the branding;
   * titling the tab then would flash the default brand over index.html's
   * neutral one.
   */
  private readonly pageTitle = signal<string | undefined | null>(null);

  constructor() {
    super();
    effect(() => {
      const page = this.pageTitle();
      const brandName = this.branding.configuredName();
      if (page === null) return;

      this.title.setTitle(
        page || brandName
          ? formatTitle(page, brandName ?? DEFAULT_BRANDING.name)
          : DEFAULT_BRANDING.title
      );
    });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.pageTitle.set(this.buildTitle(snapshot));
  }
}
